import React from 'react';
import { Button, Modal, Tooltip } from 'antd';
import {
  CheckOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  LockOutlined,
  PlayCircleFilled,
  RightOutlined,
} from '@ant-design/icons';
import { MdOutlineOndemandVideo } from 'react-icons/md';
import './must-watch-video.less';
import useIsPhoneWidth from '../../hooks/useIsPhoneWidth';
import { VideoType } from '../videoPreview/constants';
import { EngagementModelType, EngagementPanel, LinkifiedText } from '../engagement';
import {
  MustWatchVideoNotification,
  MustWatchVideoTranslations,
} from './constants';

// The modal sits above the interaction shield (z-index 1500 in the .less file).
// Tooltips render in their own portal on <body> with antd's default z-index of
// 1070, i.e. behind this modal - they have to be lifted above it explicitly or
// they show up dimmed through the modal instead of on top of it.
const MODAL_Z_INDEX = 2000;
const TOOLTIP_Z_INDEX = MODAL_Z_INDEX + 10;

interface Props {
  open: boolean;
  notification: MustWatchVideoNotification;
  t: MustWatchVideoTranslations;
  isDoneLoading?: boolean;
  /** required + unseen → no close affordance, "Done" is the only way out */
  isBlocking?: boolean;
  /** size of the unseen must-watch queue the arrows navigate (0 = no arrows) */
  queueLength?: number;
  queueIndex?: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  onDone: () => void;
  /** the queue itself — phones list the rest of it under "Next video" */
  queue?: MustWatchVideoNotification[];
  /** jump to a queue position (same navigation the arrows do) */
  onJump?: (index: number) => void;
}

const MustWatchVideoModal: React.FC<Props> = ({
  open,
  notification,
  t,
  isDoneLoading,
  isBlocking,
  queueLength = 0,
  queueIndex = -1,
  onPrev,
  onNext,
  onClose,
  onDone,
  queue = [],
  onJump,
}) => {
  const isPhone = useIsPhoneWidth();
  const hasOwnTitle = !!notification.title;
  const showArrows = queueLength > 1 && queueIndex >= 0;
  const canGoPrev = showArrows && queueIndex > 0;
  const canGoNext = showArrows && queueIndex < queueLength - 1;

  const renderVideo = () => {
    if (notification.videoType === VideoType.MP4 && notification.videoPath) {
      return (
        <video
          key={notification.id}
          controls
          autoPlay
          // iOS: play in the page (pinned player) instead of forcing fullscreen
          playsInline
          src={notification.videoPath}
        />
      );
    }
    if (notification.embedSrc) {
      return (
        <iframe
          key={notification.id}
          className="mwv-video-iframe"
          src={notification.embedSrc}
          title={notification.title || t.title}
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      );
    }
    return (
      <div className="mwv-video-fallback">
        <MdOutlineOndemandVideo size={48} />
        <span>{t.videoUnavailable}</span>
      </div>
    );
  };

  // ── Phones: a media player screen (dark) ──
  //   ━━━━ ━━━━ ────                 queue progress (stories-style)
  //   (● MUST WATCH)     ‹ 1 of 3 ›   ✕ (only when dismissable)
  //   ┌──────────────────────────────┐
  //   │          video 16:9          │  pinned while the text scrolls
  //   └──────────────────────────────┘
  //   Title
  //   New feature — required viewing
  //   description
  //   NEXT VIDEO
  //   [thumb] Title of the next one   ›   rest of the queue, tap to jump
  //   🔒 hint · [ ✓ Done, I watched it ]  pinned bottom bar
  const upNext =
    isPhone && showArrows ? queue.slice(queueIndex + 1) : [];

  const phoneProgress =
    isPhone && showArrows ? (
      <div className="mwv-progress" aria-hidden="true">
        {queue.map((item, index) => (
          <span
            key={item.id}
            className={`mwv-progress__seg${
              index < queueIndex ? ' is-done' : ''
            }${index === queueIndex ? ' is-current' : ''}`}
          />
        ))}
      </div>
    ) : null;

  const phoneUpNext =
    upNext.length > 0 ? (
      <div className="mwv-upnext">
        <div className="mwv-upnext__title">{t.nextVideo}</div>
        {upNext.map((item, offset) => (
          <button
            key={item.id}
            type="button"
            className="mwv-upnext__item"
            onClick={() => onJump?.(queueIndex + 1 + offset)}
          >
            <span className="mwv-upnext__thumb">
              {item.thumbnail ? (
                <img src={item.thumbnail} alt="" loading="lazy" />
              ) : (
                <MdOutlineOndemandVideo size={20} />
              )}
              <PlayCircleFilled className="mwv-upnext__play" />
            </span>
            <span className="mwv-upnext__text">
              <span className="mwv-upnext__name">{item.title || t.title}</span>
              {!!item.description && (
                <span className="mwv-upnext__desc">{item.description}</span>
              )}
            </span>
            <RightOutlined className="mwv-upnext__chevron" />
          </button>
        ))}
      </div>
    ) : null;

  // ── Shared pieces (desktop card and phone player use the same ones) ──
  const badge = (
    <span
      className={`mwv-modal-badge ${notification.isMustWatch ? '' : 'is-optional'}`}
    >
      <span className="mwv-badge-dot" />
      {notification.isMustWatch ? t.badge : t.optionalLabel}
    </span>
  );

  const nav = showArrows ? (
    <div className="mwv-modal-nav">
      <Tooltip title={isPhone ? undefined : t.prevVideo} zIndex={TOOLTIP_Z_INDEX}>
        <Button
          type="text"
          size="small"
          icon={<LeftOutlined />}
          disabled={!canGoPrev}
          onClick={onPrev}
          aria-label={t.prevVideo}
        />
      </Tooltip>
      <span className="mwv-modal-counter">
        {queueIndex + 1} {t.counterOf} {queueLength}
      </span>
      <Tooltip title={isPhone ? undefined : t.nextVideo} zIndex={TOOLTIP_Z_INDEX}>
        <Button
          type="text"
          size="small"
          icon={<RightOutlined />}
          disabled={!canGoNext}
          onClick={onNext}
          aria-label={t.nextVideo}
        />
      </Tooltip>
    </div>
  ) : null;

  const heading = (
    <>
      <h3 className="mwv-modal-heading">
        {hasOwnTitle ? notification.title : t.title}
      </h3>
      {hasOwnTitle && <p className="mwv-modal-subheading">{t.title}</p>}
    </>
  );

  const video = (
    <div className="mwv-video-stage">
      <div className="mwv-video-wrapper">{renderVideo()}</div>
    </div>
  );

  const description = notification.description ? (
    <p className="mwv-modal-description">
      <LinkifiedText text={notification.description} />
    </p>
  ) : null;

  // reactions + comments of the video (compact: the latest comment, the thread on click)
  const engagement = (
    <EngagementPanel
      modelType={EngagementModelType.VIDEO_NOTIFICATION}
      modelId={notification.id}
      compact
      zIndex={MODAL_Z_INDEX + 10}
      className="mwv-modal-engagement"
    />
  );

  const footer = (
    <div className="mwv-modal-footer">
      {isBlocking ? (
        <span className="mwv-modal-footer-hint">
          {isPhone ? <LockOutlined /> : <InfoCircleOutlined />}
          {t.blockedHint}
        </span>
      ) : (
        <span className="mwv-modal-footer-hint" />
      )}

      {notification.isWatched ? (
        <Button size="large" className="mwv-done-btn" onClick={onClose}>
          {t.closeLabel}
        </Button>
      ) : (
        <Button
          type="primary"
          size="large"
          className="mwv-done-btn"
          icon={<CheckOutlined />}
          loading={isDoneLoading}
          onClick={onDone}
        >
          {t.done}
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      // bo-no-sheet: the Backoffice turns every modal into a generic bottom
      // sheet on phones (top padding, grabber, close placement) — this player
      // is its own sheet, so it opts out (the class means nothing in B2C)
      className={`must-watch-video-modal bo-no-sheet${isPhone ? ' is-phone' : ''}`}
      // the wrap is flex-centered in the .less file instead of antd's
      // `centered` prop, which uses an inline-block trick that leaves a
      // scrollbar once the modal is a full 100vh tall
      wrapClassName="must-watch-video-modal-wrap bo-no-sheet"
      closable={!isBlocking}
      maskClosable={false}
      keyboard={false}
      onCancel={onClose}
      width={880}
      title={null}
      footer={null}
      zIndex={MODAL_Z_INDEX}
      // phones: the player slides up like a native media screen
      transitionName={isPhone ? 'ant-move-down' : undefined}
    >
      {isPhone ? (
        <div className="mwv-modal-card mwv-phone">
          {/* fixed: bar + video; only the text below scrolls */}
          <div className="mwv-phone__top">
            {phoneProgress}
            <div className="mwv-modal-header-top">
              {badge}
              {nav}
            </div>
          </div>
          {video}
          <div className="mwv-phone__scroll">
            {heading}
            {description}
            {engagement}
            {phoneUpNext}
          </div>
          {footer}
        </div>
      ) : (
        <div className="mwv-modal-card">
          <div className="mwv-modal-header">
            <div className="mwv-modal-header-top">
              {badge}
              {nav}
            </div>
            {heading}
          </div>
          {video}
          {description}
          {engagement}
          {footer}
        </div>
      )}
    </Modal>
  );
};

export default MustWatchVideoModal;
